import { ResponseProps } from 'types/CommonTypes'
import {
  CarpoolType,
  CommunityCarpoolOutputProps,
  CommunityCarpoolRequestOutputProps,
  CommunityCarpoolsQueryParams,
  CommunityCarpoolStatsOutputProps,
  CommunityCarpoolStatsQueryParams,
  CommunityStudentCarpoolOutputProps,
  CommunityStudentCarpoolsQueryParams,
  CommunityType,
  DefaultCarpoolType,
  EnrolledCommunityOutputProps,
  UpdateCommunityStudentCarpoolDetailInputProps,
  UpdateCommunityStudentCarpoolInputProps,
  UpdateCommunityStudentCarpoolRequestInputProps,
} from 'types/CommunityTypes'
import { buildQueryString, httpBearerAuth, processError } from './requests'

/**
 * Gets enrolled communities
 * @param communityType - Community type
 * @returns Promise resolving to ResponseProps containing communities
 */
export const getEnrolledCommunities = async (
  communityType?: CommunityType,
): Promise<ResponseProps<{ message: string; communities: EnrolledCommunityOutputProps[] }>> => {
  try {
    const { data } = await httpBearerAuth('get', `/api/enrolledCommunities?communityType=${communityType}`)
    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

/**
 * Gets community carpool requests
 * @param comId - Community ID
 * @param poolType - Pool type
 * @param poolDate - Pool date
 * @returns Promise resolving to ResponseProps containing community carpool requests
 */
export const getCommunityCarpoolRequests = async (
  comId: string,
  poolType: CarpoolType,
  poolDate?: string,
): Promise<ResponseProps<{ message: string; carpoolRequests: CommunityCarpoolRequestOutputProps[] }>> => {
  try {
    const { data } = await httpBearerAuth(
      'get',
      `/api/carpoolRequests?poolType=${poolType}${poolDate ? `&poolDate=${poolDate}` : ''}`,
      comId,
    )
    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

/**
 * Gets community carpool stats
 * @param comId - Community ID
 * @param params - Query parameters
 * @returns Promise resolving to ResponseProps containing community carpool stats
 */
export const getCommunityCarpoolStats = async (
  comId: string,
  params: CommunityCarpoolStatsQueryParams = {},
): Promise<ResponseProps<{ message: string; carpoolStats: CommunityCarpoolStatsOutputProps }>> => {
  try {
    const queryString = buildQueryString(params)
    const { data } = await httpBearerAuth(
      'get',
      `/api/carpoolStats${queryString ? `?${queryString}` : ''}`,
      comId,
      undefined,
      true,
    )

    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

/**
 * Gets community carpools
 * @param comId - Community ID
 * @param params - Query parameters
 * @returns Promise resolving to ResponseProps containing community carpools
 */
export const getCommunityCarpools = async (
  comId: string,
  params: CommunityCarpoolsQueryParams,
): Promise<ResponseProps<{ message: string; carpools: CommunityCarpoolOutputProps[] }>> => {
  try {
    const queryString = buildQueryString(params)
    const { data } = await httpBearerAuth(
      'get',
      `/api/carpools${queryString ? `?${queryString}` : ''}`,
      comId,
      undefined,
      true,
    )

    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

/**
 * Gets community student carpools
 * @param comId - Community ID
 * @param stuId - Student ID
 * @param params - Query parameters
 * @returns Promise resolving to ResponseProps containing community student carpools
 */
export const getCommunityStudentCarpools = async (
  comId: string,
  stuId: string,
  params: CommunityStudentCarpoolsQueryParams,
): Promise<ResponseProps<{ message: string; carpools: CommunityStudentCarpoolOutputProps[] }>> => {
  try {
    const queryString = buildQueryString(params)
    const { data } = await httpBearerAuth(
      'get',
      `/api/students/${encodeURIComponent(stuId)}/carpools${queryString ? `?${queryString}` : ''}`,
      comId,
      undefined,
      true,
    )

    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

/**
 * Updates community student carpool requests
 * @param comId - Community ID
 * @param stuId - Student ID
 * @param body - Update community student carpool requests input
 * @returns Promise resolving to ResponseProps
 */
export const updateCommunityStudentCarpoolRequests = async (
  comId: string,
  stuId: string,
  body: UpdateCommunityStudentCarpoolRequestInputProps,
): Promise<ResponseProps> => {
  try {
    const { data } = await httpBearerAuth(
      'patch',
      `/api/students/${stuId}/carpoolRequests`,
      comId,
      body as unknown as Record<string, unknown>,
    )
    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

/**
 * Updates community student carpool detail
 * @param comId - Community ID
 * @param stuId - Student ID
 * @param body - Update community student carpools input
 * @returns Promise resolving to ResponseProps
 */
export const updateCommunityStudentCarpoolDetail = async (
  comId: string,
  stuId: string,
  body: UpdateCommunityStudentCarpoolDetailInputProps,
): Promise<ResponseProps> => {
  try {
    const { data } = await httpBearerAuth(
      'patch',
      `/api/students/${stuId}/carpoolDetail`,
      comId,
      body as unknown as Record<string, unknown>,
    )
    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

/**
 * Updates community student carpools
 * @param comId - Community ID
 * @param stuId - Student ID
 * @param body - Update community student carpools input
 * @returns Promise resolving to ResponseProps
 */
export const updateCommunityStudentCarpools = async (
  comId: string,
  stuId: string,
  body: UpdateCommunityStudentCarpoolInputProps,
): Promise<ResponseProps> => {
  try {
    const { data } = await httpBearerAuth(
      'patch',
      `/api/students/${stuId}/carpools`,
      comId,
      body as unknown as Record<string, unknown>,
    )
    return { data }
  } catch (error: any) {
    return { msg: processError(error), error }
  }
}

/*
 * Community type utilities
 */
export const getCommunityTypeName = (comType: CommunityType = CommunityType.SCHOOL) => {
  switch (comType) {
    case CommunityType.SCHOOL:
      return 'School'
    case CommunityType.LEA:
      return 'District'
    default:
      return comType
  }
}

/*
 * Community type utilities for various carpool types
 */
export const getCommunityCarpoolTypeLabel = (
  carpoolType: CarpoolType,
  comType: CommunityType = CommunityType.SCHOOL,
) => {
  switch (carpoolType) {
    case DefaultCarpoolType.ARRIVAL:
      return comType === CommunityType.SCHOOL ? 'Morning' : carpoolType
    case DefaultCarpoolType.DISMISSAL:
      return comType === CommunityType.SCHOOL ? 'Afternoon' : carpoolType
    default:
      return carpoolType
  }
}
